/**
 * The checklist component used by Board cards and the item page (SPEC §7.1, §7.3):
 * open rows nested up to `maxDepth`, "+N more", a Done dropdown and "+ Add item".
 */
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { openRows, type ItemsIndex } from '@terrazzo/core'
import { newRowFrom, type RowPlace } from '../lib/newRow'
import { store } from '../store/store'
import { useDerived, useItem } from '../store/hooks'
import { actions } from '../store/actions'
import { startEditing } from '../store/ui'
import { Row, ephemeral, type RowNav } from './Row'
import { DoneDropdown } from './DoneDropdown'
import { displayRows, INDENT, listStrategy, useTreeDnd } from '../dnd/TreeDnd'
import type { FlatRow } from '../dnd/projection'
import { navigate, itemPath } from '../lib/router'
import { scaled } from '../lib/scale'

export interface ChecklistProps {
  rootId: string
  scope: string
  maxDepth?: number
  showDone?: boolean
  addLabel?: string
  /** Extra props for new rows (e.g. date when used in a Day context). */
  newRowDefaults?: { date?: string }
  /** In a tab: where it's shown (a row added with Enter takes what keeps it there; see newRowFrom). */
  place?: RowPlace | null
  footer?: ReactNode
  emptyHint?: string
  /** Find on the page: show only these rows (matches and the rows above them). */
  only?: Set<string>
  /**
   * A filtered tab's card: `only` narrows it, but it still works like a card on Home — rows
   * added here stay shown (even before they match), with "+ Add item" and the Done dropdown.
   */
  full?: boolean
}

type RowInfo = FlatRow & { more: number }

function flatten(idx: ItemsIndex, rootId: string, maxDepth: number, only?: Set<string>): RowInfo[] {
  const rows = openRows(idx, rootId, maxDepth)
  return (only ? rows.filter((r) => only.has(r.id)) : rows).map((r) => ({
    id: r.id,
    depth: r.depth,
    parentId: idx.tree.parentOf.get(r.id) ?? rootId,
    more: r.more,
  }))
}

/**
 * Keep a list's rows referentially stable when a structural change elsewhere didn't
 * touch it, so unaffected cards (and their rows) skip re-rendering.
 */
const lastRows = new Map<string, RowInfo[]>()
function stableRows(key: string, next: RowInfo[]): RowInfo[] {
  const prev = lastRows.get(key)
  if (
    prev &&
    prev.length === next.length &&
    prev.every((r, i) => {
      const n = next[i]!
      return r.id === n.id && r.depth === n.depth && r.more === n.more && r.parentId === n.parentId
    })
  ) {
    return prev
  }
  lastRows.set(key, next)
  return next
}

export const Checklist = memo(function Checklist({
  rootId,
  scope,
  maxDepth = 3,
  showDone = true,
  addLabel = 'Add item',
  newRowDefaults,
  place,
  footer,
  emptyHint,
  only: onlyIn,
  full = false,
}: ChecklistProps) {
  const dnd = useTreeDnd()
  // Rows added in this list while it's narrowed (a tab's filter) stay shown for this visit.
  const [added, setAdded] = useState<ReadonlySet<string>>(() => new Set())
  const keep = useCallback((id: string | null | undefined) => {
    if (id) setAdded((cur) => new Set(cur).add(id))
    return id ?? null
  }, [])
  const only = useMemo(() => (onlyIn && added.size ? new Set([...onlyIn, ...added]) : onlyIn), [onlyIn, added])
  // Checked-off items only go to the Done dropdown when the card tucks them away.
  const collapsed = (useItem(rootId)?.doneStyle ?? 'collapse') === 'collapse'
  const base = useDerived(() => stableRows(`${scope}:${rootId}:${maxDepth}`, flatten(store.idx, rootId, maxDepth, only)), [rootId, maxDepth, scope, only])
  const moreOf = useMemo(() => new Map(base.map((r) => [r.id, r.more])), [base])

  useEffect(() => {
    if (!dnd) return
    return dnd.register({ rootId, maxDepth: maxDepth - 1, getRows: () => flatten(store.idx, rootId, maxDepth, only) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnd?.register, rootId, maxDepth, only])

  const st = dnd?.state
  const rows = st ? displayRows(base, rootId, st) : base
  const ids = useMemo(() => rows.map((r) => r.id), [rows])

  const { setNodeRef, isOver } = useDroppable({ id: `list:${scope}:${rootId}`, data: { type: 'list', root: rootId } })

  const nav: RowNav = useMemo(() => {
    const index = (id: string) => rows.findIndex((r) => r.id === id)
    // A card's rows as you see them here (in a tab, just the ones it shows).
    const visibleIn = (parent: string) => (store.tree.childrenOf.get(parent) ?? []).filter((c) => !store.tree.inTrash.has(c) && (!only || only.has(c)))
    return {
      prev: (id) => rows[index(id) - 1]?.id ?? null,
      next: (id) => rows[index(id) + 1]?.id ?? null,
      prevSibling: (id) => {
        const i = index(id)
        const d = rows[i]?.depth ?? 0
        for (let j = i - 1; j >= 0; j--) {
          if (rows[j]!.depth < d) return null
          if (rows[j]!.depth === d) return rows[j]!.depth + 1 < maxDepth ? rows[j]!.id : null
        }
        return null
      },
      canOutdent: (id) => (rows[index(id)]?.depth ?? 0) > 0,
      hasOpenChildren: (id) => {
        const i = index(id)
        return i >= 0 && (rows[i + 1]?.depth ?? -1) > rows[i]!.depth
      },
      addAfter: (id, title, asFirstChild) => {
        const cur = store.get(id)
        const done = cur?.done === null ? null : false
        // It takes what keeps it in this card (what the rows you see here share, what the tab needs).
        const parent = asFirstChild ? id : (store.tree.parentOf.get(id) ?? rootId)
        const from = newRowFrom(id, visibleIn(parent), place)
        if (asFirstChild) return keep(actions.addItem({ parentId: id, at: 'start', title, done, ...newRowDefaults, ...from }))
        return keep(actions.addItem({ parentId: parent, afterId: id, title, done, ...newRowDefaults, ...from }))
      },
      addManyAfter: (id, titles) => {
        const parent = store.tree.parentOf.get(id) ?? rootId
        const ids = actions.addRows({ parentId: parent, afterId: id }, titles, { ...newRowDefaults, ...newRowFrom(id, visibleIn(parent), place) })
        ids.forEach(keep)
        return ids
      },
    }
  }, [rows, rootId, maxDepth, newRowDefaults, keep, place, only])

  const addAtEnd = () => {
    const id = actions.addItem({ parentId: rootId, at: 'end', title: '', ...newRowDefaults })
    keep(id)
    ephemeral.add(id)
    startEditing(scope, id, 'end')
  }

  return (
    <div ref={setNodeRef} className={`rounded-lg transition-colors ${isOver && st?.fromRoot !== rootId ? 'bg-surface-2' : ''}`} data-list={rootId}>
      <SortableContext items={ids} strategy={listStrategy(rootId, st)}>
        {rows.map((r) => (
          <SortableRow
            key={r.id}
            id={r.id}
            depth={r.depth}
            rootId={rootId}
            scope={scope}
            nav={nav}
            more={moreOf.get(r.id) ?? 0}
            dragDepth={st?.activeId === r.id && st.overRoot === rootId && st.projection ? st.projection.depth : undefined}
          />
        ))}
      </SortableContext>
      {rows.length === 0 && emptyHint && <div className="py-1.5 pl-0.5 text-faint">{emptyHint}</div>}
      {/* The last line: + Add item, and what's done on the same line (SPEC §7.10). */}
      {(!only || full) &&
        (() => {
          const add = (
            <button type="button" onClick={addAtEnd} className="lab flex h-8 items-center pl-0.5 text-left text-muted hover:text-fg">
              + {addLabel}
            </button>
          )
          return showDone && collapsed ? <DoneDropdown rootId={rootId} scope={scope} lead={add} /> : add
        })()}
      {showDone && collapsed && only && !full && <DoneDropdown rootId={rootId} scope={scope} />}
      {footer}
    </div>
  )
})

const SortableRow = memo(function SortableRow({
  id,
  depth,
  rootId,
  scope,
  nav,
  more,
  dragDepth,
}: {
  id: string
  depth: number
  rootId: string
  scope: string
  nav: RowNav
  more: number
  dragDepth?: number
}) {
  const s = useSortable({ id, data: { type: 'row', root: rootId } })
  const style = { transform: CSS.Translate.toString(s.transform), transition: s.transition }
  return (
    <>
      <Row
        id={id}
        depth={depth}
        scope={scope}
        nav={nav}
        dragDepth={dragDepth}
        indentWidth={INDENT}
        sortable={{
          setNodeRef: s.setNodeRef,
          listeners: s.listeners as Record<string, unknown>,
          attributes: s.attributes as unknown as Record<string, unknown>,
          style,
          isDragging: s.isDragging,
        }}
      />
      {more > 0 && !s.isDragging && (
        <button type="button" onClick={() => navigate(itemPath(id))} className="lab block h-6 text-left text-muted hover:text-fg" style={{ paddingLeft: scaled((depth + 1) * INDENT + 30) }}>
          +{more} more
        </button>
      )}
    </>
  )
})
